import React from 'react';
import styled from 'styled-components';

import {
  Colors,
  FontFamilies,
  FontSizes,
  Theme,
} from 'common';

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (y) =>
  (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

const daysInMonth = (year, month) => {
  if (!year || !month) return 31;
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  return DAYS_IN_MONTH[month - 1] || 31;
};

const parseIsoDate = (v) => {
  if (!v || typeof v !== 'string') return null;
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (mo < 1 || mo > 12 || d < 1) return null;
  const maxD = daysInMonth(y, mo);
  if (d > maxD) return null;
  const check = new Date(y, mo - 1, d);
  if (
    check.getFullYear() !== y ||
    check.getMonth() !== mo - 1 ||
    check.getDate() !== d
  ) {
    return null;
  }
  return { y, m: mo, d };
};

const pad2 = (n) => String(n).padStart(2, '0');

const Row = styled.div`
  display: flex;
  gap: 8px;
  width: ${(props) => props.width || '100%'};
  align-items: stretch;
`;

const SelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    width: 6px;
    height: 6px;
    border-right: 2px solid ${Colors.DarkGrey};
    border-bottom: 2px solid ${Colors.DarkGrey};
    transform: translateY(-70%) rotate(45deg);
    pointer-events: none;
    opacity: 0.9;
  }

  &:focus-within::after {
    border-color: ${Colors.PrimaryOne || Colors.DarkGrey};
    opacity: 1;
  }
`;

const SelectEl = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 32px 0 10px;
  border: ${Theme.TextInput.Border};
  border-radius: ${Theme.TextInput.BorderRadius};
  background-color: ${Theme.TextInput.Background};
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Regular};
  font-size: ${FontSizes.Small};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &::-ms-expand {
    display: none;
  }

  &:focus {
    outline: none;
    border-color: ${Colors.PrimaryOne || Theme.TextInput.Border};
  }
`;

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Lightweight date picker: three native selects, no extra npm deps.
 * Value / onChange use ISO date strings YYYY-MM-DD or ''.
 * Year range defaults to (currentYear - 120) … currentYear unless overridden.
 */
export const DateSelect = (props) => {
  const {
    value,
    onChange,
    width = '100%',
    minYear: minYearProp,
    maxYear: maxYearProp,
  } = props;

  const currentYear = new Date().getFullYear();
  const minYear = minYearProp ?? currentYear - 120;
  const maxYear = maxYearProp ?? currentYear;

  const parsed = React.useMemo(() => parseIsoDate(value), [value]);

  const [y, setY] = React.useState(parsed?.y ?? '');
  const [m, setM] = React.useState(parsed?.m ?? '');
  const [d, setD] = React.useState(parsed?.d ?? '');

  React.useEffect(() => {
    const p = parseIsoDate(value);
    setY(p?.y ?? '');
    setM(p?.m ?? '');
    setD(p?.d ?? '');
  }, [value]);

  const emit = React.useCallback(
    (nextY, nextM, nextD) => {
      if (nextY && nextM && nextD) {
        const maxD = daysInMonth(nextY, nextM);
        const day = Math.min(nextD, maxD);
        onChange(`${nextY}-${pad2(nextM)}-${pad2(day)}`);
        setY(nextY);
        setM(nextM);
        setD(day);
      } else {
        setY(nextY);
        setM(nextM);
        setD(nextD);
        onChange('');
      }
    },
    [onChange]
  );

  const onYearChange = (e) => {
    const v = e.target.value;
    const nextY = v === '' ? '' : parseInt(v, 10);
    if (v !== '' && Number.isNaN(nextY)) return;
    if (nextY && m && d) {
      const maxD = daysInMonth(nextY, m);
      emit(nextY, m, Math.min(d, maxD));
    } else {
      emit(nextY, m, d);
    }
  };

  const onMonthChange = (e) => {
    const v = e.target.value;
    const nextM = v === '' ? '' : parseInt(v, 10);
    if (v !== '' && Number.isNaN(nextM)) return;
    if (y && nextM && d) {
      const maxD = daysInMonth(y, nextM);
      emit(y, nextM, Math.min(d, maxD));
    } else {
      emit(y, nextM, d);
    }
  };

  const onDayChange = (e) => {
    const v = e.target.value;
    const nextD = v === '' ? '' : parseInt(v, 10);
    if (v !== '' && Number.isNaN(nextD)) return;
    emit(y, m, nextD);
  };

  const maxDay =
    y && m ? daysInMonth(y, m) : 31;

  const yearOptions = React.useMemo(() => {
    const descending = [];
    for (let yr = maxYear; yr >= minYear; yr -= 1) {
      descending.push(yr);
    }
    return descending;
  }, [minYear, maxYear]);

  return (
    <Row width={width}>
      <SelectWrapper>
        <SelectEl
          aria-label="Day"
          value={d === '' ? '' : String(d)}
          onChange={onDayChange}
        >
          <option value="">Day</option>
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((day) => (
            <option key={day} value={String(day)}>
              {day}
            </option>
          ))}
        </SelectEl>
      </SelectWrapper>
      <SelectWrapper>
        <SelectEl
          aria-label="Month"
          value={m === '' ? '' : String(m)}
          onChange={onMonthChange}
        >
          <option value="">Month</option>
          {MONTH_LABELS.map((label, idx) => (
            <option key={label} value={String(idx + 1)}>
              {label}
            </option>
          ))}
        </SelectEl>
      </SelectWrapper>
      <SelectWrapper>
        <SelectEl
          aria-label="Year"
          value={y === '' ? '' : String(y)}
          onChange={onYearChange}
        >
          <option value="">Year</option>
          {yearOptions.map((yr) => (
            <option key={yr} value={String(yr)}>
              {yr}
            </option>
          ))}
        </SelectEl>
      </SelectWrapper>
    </Row>
  );
};
